import React, { Component, type ReactNode, type Context as ContextType } from 'react';
import omit from 'lodash/omit';
import createSafeContext from '@atlassian/jira-common-util-create-safe-context/src/index.tsx';
import {
	type Data,
	type ItemManagers,
	type ManagerKey,
	type GetItemManager,
	type RegisterItemManager,
	type ItemManagerMethods,
	fromManagerKey,
} from './types.tsx';

const Context: ContextType<Data> = createSafeContext('ItemManagers');

export type Props = {
	children: ReactNode;
};

type State = {
	managers: ItemManagers;
};

type PendingManagers = Record<ManagerKey, ((arg1: ItemManagerMethods) => void)[]>;

export const REJECT_TIMEOUT = 30 * 1000;

// eslint-disable-next-line jira/react/no-class-components
export class Provider extends Component<Props, State> {
	state = {
		managers: {},
	};

	getManager: GetItemManager = (managerKey) => {
		const { managers } = this.state;
		// @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{}'.
		const manager = managers[managerKey];
		if (manager) {
			return manager;
		}

		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		return new Promise((resolve, reject: (error?: any) => void) => {
			const message = `Timeout. "${fromManagerKey(
				managerKey,
			)}" wasn't registered after ${REJECT_TIMEOUT}ms`;

			const rejectTimeoutId = setTimeout(() => reject(new Error(message)), REJECT_TIMEOUT);
			const { pendingManagers } = this;

			pendingManagers[managerKey] || (pendingManagers[managerKey] = []);
			pendingManagers[managerKey].push((methods) => {
				clearTimeout(rejectTimeoutId);
				resolve(methods);
			});
		});
	};

	registerManager: RegisterItemManager = (managerKey, methods) => {
		this.setState((previousState) => {
			if (methods == null) {
				return { managers: omit(previousState.managers, managerKey) };
			}

			const managerResolvers = this.pendingManagers[managerKey];

			if (managerResolvers && managerResolvers.length) {
				managerResolvers.forEach((resolve) => resolve(methods));
				managerResolvers.length = 0;
			}

			return {
				managers: {
					...previousState.managers,
					[managerKey]: Promise.resolve(methods),
				},
			};
		});
	};

	pendingManagers: PendingManagers = {};

	manager = {
		getManager: this.getManager,
		registerManager: this.registerManager,
	};

	render() {
		return <Context.Provider value={this.manager}>{this.props.children}</Context.Provider>;
	}
}

export const { Consumer } = Context;
